<template>
	<div>
		<div class="table_head table_head_filter" style="height: 65px;">
			<div class="flex flex_center">
				<md-switch v-model="showAllColumns">
					{{ $t("common.table.switch.showAllColumns") }}
				</md-switch>
				<AlfaButtonGroup :status="(alfaStatus || {}).status" @show="showAlfaSettingsModal" @start="confirmStartAlfa" @stop="stopAlfa"/>
				<div style="margin-left: 4rem" v-if="lastStatisticsUpdateTime">
					<i class="fa fa-info-circle secondary_color" aria-hidden="true" style="margin-right: 0.5rem"></i>
					<span>
						{{$t("investor.exchange.lastStatisticsUpdate", { updateTime: lastStatisticsUpdateTime }) }}
					</span>
				</div>
				<!-- DEV-6132: Hide until we have TCI again
				<div style="margin-left: 4rem" v-if="lastTCILimitsUpdateTime">
					<i class="fa fa-info-circle secondary_color" aria-hidden="true" style="margin-right: 0.5rem"></i>
					<span>
						{{$t("investor.exchange.lastTCILimitsUpdate", { updateTime: lastTCILimitsUpdateTime }) }}
					</span>
				</div>
				-->
			</div>
			<div class="flex tcilogoandbutton">
				<!-- DEV-6132: Hide until we have TCI again
				<div class="text_right">
					<img src="../../../../../assets/img/one_underwriting_logo.png" style="width: 70px;"/>
				</div>
				<ActivateTciButton v-if="hideTci"/>
				-->

				<div class="text_right" style="width: 350px;">
					<span>{{ $t("investor.exchange.table.selectedBids") }}</span>
					<br/>
					<span class="font_bold accent_color">{{ totalSelectedBidAmount }}</span>
					<!-- DEV-6132: Hide until we have TCI again
					<br/>
					<span class="accent_color">
						{{ $t("investor.exchange.table.selectedPremiums", { premiums: totalSelectedBidPremiums }) }}
					</span>
					-->
				</div>
			</div>
		</div>
		<div class="flex filter_table">
			<div class="flex flex_column w_100">
				<ag-item :rowData="auctionList"
						 :columnDefs="columnDefs"
						 @row-select="onRowSelect"
						 v-on:sortChanged="sortChanged"
						 :with-pagination="false"
						 :loading-warning-text="accountsWarningText"
						 :key="accountsWarningText"
						 :excelStyles="excelStyles"
						 ref="exchangeReceivablesTable"
				></ag-item>
			</div>
			<ExchangeReceivablesFilter :rating-config="ratingConfig" @apply-filter="applyFilter"/>
		</div>
		<AlfaSettingsModal ref="alfaSettingsModal" :account="activeAccount" :tci-confirmation-status="tciConfirmationStatus" @close="loadAlfaStatus"/>
		<ExchangeReceivableNoteModel ref="exchangeReceivableNoteModal"/>
		<BidsResponseDialog ref="bidsResponseDialog"/>
		<ProspectiveReceivableSnapshotModal ref="prospectiveReceivableSnapshotModal"></ProspectiveReceivableSnapshotModal>
	</div>
</template>

<script>
import AgItem from "@/components/templates/agGrid/AgItem.vue";
import BidsResponseDialog from "src/components/templates/investor/exchange/receivables/BidsResponseDialog";
import ExchangeReceivableNoteModel
	from "@/components/templates/investor/exchange/receivables/ExchangeReceivableNoteModel";
import {mapActions, mapGetters} from "vuex";
import {exchangeReceivablesApi} from "@/common/api/exchangeReceivablesApi";
import {formatAsMoneyAmount, roundToScale} from "@/common/format";
import {
	asDecimal,
	asIndustryText,
	asLocalizedDate,
	asMoneyAmount,
	asPercentage
} from "@/components/templates/agGrid/formatter/valueFormatters";
import {enumDef} from "@/common/enumDefs";
import ExchangeReceivablesFilter from "@/components/templates/investor/exchange/receivables/ExchangeReceivablesFilter";
import {asNullIfOtherFieldIsTrue} from "@/components/templates/agGrid/getter/valueGetters";
import AlfaSettingsModal from "@/components/templates/investor/exchange/alfa/AlfaSettingsModal";
import {alfaApi} from "@/common/api/alfaApi";
import AlfaButtonGroup from "@/components/templates/investor/exchange/alfa/AlfaButtonGroup.vue";
import {hasAccess} from "@/common/permission/permission";
import ActivateTciButton from "@/components/templates/investor/exchange/receivables/ActivateTciButton.vue";
import ProspectiveReceivableSnapshotModal
	from "@/components/templates/investor/riskAssessment/marketSounding/ProspectiveReceivableSnapshotModal.vue";

export default {
	components: {
		ProspectiveReceivableSnapshotModal,
		ActivateTciButton,
		AlfaButtonGroup,
		AlfaSettingsModal,
		ExchangeReceivablesFilter,
		ExchangeReceivableNoteModel,
		AgItem,
		BidsResponseDialog,
	},
	props: {
		activeAccount: { type: Object, default: null },
		accountsWarningText: { type: String, default: "" },
		ratingConfig: { type: Object, default: null },
		tciConfirmationStatus: { type: Number, default: enumDef.creditInsurancePolicyHolderStatus.NONE },
		ready: { type: Boolean, default: false }
	},
	data() {
		return {
			auctionList: null,
			filters: [],
			sorting: [],
			columnDefs: [],
			showAllColumns: false,
			lastStatisticsUpdateTime: null,
			lastTCILimitsUpdateTime: null,
			alfaStatus: null
		};
	},
	computed: {
		...mapGetters(["exchangeCheckedRows"]),
		totalSelectedBidAmount() {
			let amount = this.exchangeCheckedRows
				.map((data) => data.finalBuyPrice)
				.reduce((bid1, bid2) => bid1 + bid2, 0);

			return formatAsMoneyAmount(amount, {
				currency: this.activeAccount ? this.activeAccount.tbCurrType : null
			});
		},
		totalSelectedBidPremiums() {
			return formatAsMoneyAmount(this.getOverallPremiums());
		},
		activeAccountId() {
			return this.activeAccount.id;
		},
		hideTrafficLight() {
			return this.$sessionStorage.get("localeUser").hideTrafficLight;
		},
		canBuyTci() {
			return enumDef.creditInsurancePolicyHolderStatus.canBuyTci(this.tciConfirmationStatus);
		},
		canOnlyViewTci() {
			return enumDef.creditInsurancePolicyHolderStatus.hasRequestedAccess(this.tciConfirmationStatus);
		},
		hideTci() {
			return !enumDef.creditInsurancePolicyHolderStatus.hasTciAccess(this.tciConfirmationStatus);
		}
	},
	beforeMount() {
		if (!this.ready) {
			return;
		}
		
		this.setColumnDefs();
		this.getList();
	},
	methods: {
		getOverallPremiums() {
			return this.exchangeCheckedRows
				.filter(data => data.useInsurance)
				.map((data) => data.insuranceGrossPremiumAmount || 0)
				.reduce((value1, value2) => value1 + value2, 0);
		},
		...mapActions(["setActionCheckedRowsInExchange", "clearActionCheckedRowsInExchange", "setActionActiveAccount"]),
		setColumnDefs() {
			this.columnDefs = [
				{
					headerName: this.$t("investor.exchange.table.header.marketSounding"),
					children: [
						{
							headerName: this.$t("common.status"),
							field: "marketSoundingStatus",
							minWidth: 75,
							maxWidth: 75,
							cellRendererFramework: "MarketSoundingDecisionStatus",
							cellRendererParams: {
								checkDecision: this.checkMarketSoundingDecision
							},
							sortable: true
						},
					]
				},
				{
					headerName: this.$t("common.debtor"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.debtor.tooltip"),
					},
					children: [
						{
							headerName: this.$t("common.debtor.name"),
							field: "debtorName",
							minWidth: 175,
							nameType: "debtorName",
							idType: "debtorId",
							investorCompanyConfigStatusType: "investorDebtorConfigStatus",
							cellRendererFramework: "CompanyTem",
							sortable: true
						},
						{
							headerName: this.$t("common.industry"),
							field: "debtorIndustryNaceCode",
							minWidth: 180,
							valueFormatter: asIndustryText({ detailLevel: enumDef.industryDetailLevel.Section }),
							cellRendererFramework: "GeneralShortenedDisplay",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating1,
							headerName: this.ratingConfig.nameRating1 && this.ratingConfig.nameRating1
								? this.ratingConfig.nameRating1
								: this.$t("investor.common.table.header.customRating1"),
							field: "debtorRating1",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating2,
							headerName: this.ratingConfig.nameRating2 && this.ratingConfig.nameRating2
								? this.ratingConfig.nameRating2
								: this.$t("investor.common.table.header.customRating2"),
							field: "debtorRating2",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating3,
							headerName: this.ratingConfig.nameRating3 && this.ratingConfig.nameRating3
								? this.ratingConfig.nameRating3
								: this.$t("investor.common.table.header.customRating3"),
							field: "debtorRating3",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							headerName: this.$t("investor.exchange.table.header.country"),
							field: "debtorCountry",
							minWidth: 90,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.city"),
							field: "debtorCity",
							minWidth: 75,
							columnGroupShow: "open",
						},
						{
							headerName: this.$t("common.postcode"),
							field: "debtorPostcode",
							minWidth: 100,
							columnGroupShow: "open",
						},
						{
							headerName: this.$t("common.street"),
							field: "debtorStreet",
							minWidth: 75,
							columnGroupShow: "open",
						},
						{
							headerName: this.$t("common.vatId"),
							field: "debtorVatNumber",
							minWidth: 75,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.all"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.all.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "numDebtorAll",
							cellClass: "align-right",
							minWidth: 130,
							maxWidth: 130,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.latePaymentDays"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.latePaymentDays.tooltip"),
								showTooltipIcon: true,
							},
							headerClass: "align-right",
							field: "debtorMeanLpd",
							cellClass: "align-right",
							valueFormatter: asDecimal({ showEmptyAsNotAvailable: true }),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "numDebtorUnpaid",
							cellClass: "align-right",
							minWidth: 135,
							maxWidth: 135,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "numDebtorUnpaidAndOverdue",
							cellClass: "align-right",
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "debtorAverageDaysAfterDueDate",
							cellClass: "align-right",
							valueFormatter: asDecimal( { showEmptyAsNotAvailable: true }),
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "numDebtorFullyPaid",
							cellClass: "align-right",
							minWidth: 140,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageLpdSettled"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageLpdSettled.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "debtorAverageLpdSettled",
							cellClass: "align-right",
							valueFormatter: asDecimal({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmount",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmountUnpaid",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmountUnpaidAndOverdue",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmountFullyPaid",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmountFullyPaidAndInDelay",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						}
					]
				},
				{
					headerName: this.$t("common.seller"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.seller.tooltip"),
					},
					children: [
						{
							headerName: this.$t("common.seller.name"),
							minWidth: 175,
							field: "sellerName",
							nameType: "sellerName",
							idType: "sellerId",
							investorCompanyConfigStatusType: "investorSellerConfigStatus",
							cellRendererFramework: "CompanyTem",
							sortable: true
						},
						{
							headerName: this.$t("common.industry"),
							field: "sellerIndustryNaceCode",
							minWidth: 180,
							valueFormatter: asIndustryText({ detailLevel: enumDef.industryDetailLevel.Section }),
							cellRendererFramework: "GeneralShortenedDisplay",
							sortable: true
						},
						{
							headerName: this.$t("common.partner"),
							field: "partnerName",
							minWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating1,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating1
								: this.$t("investor.common.table.header.customRating1"),
							field: "sellerRating1",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating2,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating2
								: this.$t("investor.common.table.header.customRating2"),
							field: "sellerRating2",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating3,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating3
								: this.$t("investor.common.table.header.customRating3"),
							field: "sellerRating3",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.country"),
							field: "sellerCountry",
							minWidth: 90,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},

						{
							headerName: this.$t("common.city"),
							field: "sellerCity",
							minWidth: 75,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.postcode"),
							field: "sellerPostcode",
							minWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.street"),
							field: "sellerStreet",
							minWidth: 75,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.vatId"),
							field: "sellerVatNumber",
							minWidth: 75,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.all"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.all.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "numSupplierAll",
							cellClass: "align-right",
							minWidth: 130,
							maxWidth: 130,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "numSupplierUnpaid",
							cellClass: "align-right",
							minWidth: 135,
							maxWidth: 135,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "numSupplierUnpaidAndOverdue",
							cellClass: "align-right",
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "supplierAverageDaysAfterDueDate",
							cellClass: "align-right",
							valueFormatter: asDecimal({ showEmptyAsNotAvailable: true }),
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "numSupplierFullyPaid",
							cellClass: "align-right",
							minWidth: 140,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageLpdSettled"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageLpdSettled.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "supplierAverageLpdSettled",
							cellClass: "align-right",
							valueFormatter: asDecimal({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmount",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmountUnpaid",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmountUnpaidAndOverdue",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmountFullyPaid",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmountFullyPaidAndInDelay",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						}
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.riskAnalysis"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.riskAnalysis.tooltip"),
					},
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.dsrConfirmed"),
							field: "dsrConfirmed",
							cellClass: "align-right",
							minWidth: 130,
							maxWidth: 130,
							cellRendererFramework: "BooleanAsIcon",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.directPay"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.directPay.tooltip"),
								showTooltipIcon: true,
							},
							headerClass: "align-right",
							field: "assessmentDirectPay",
							cellClass: "align-right",
							minWidth: 140,
							maxWidth: 140,
							cellRendererFramework: "BooleanAsIcon",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.dsrExistsSince"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.dsrExistsSince.tooltip"),
								showTooltipIcon: true,
							},
							field: "dsrExistsSince",
							valueFormatter: asLocalizedDate( {showEmptyAsNotAvailable: true }),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.all"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.all.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentTotalCases",
							cellClass: "align-right",
							minWidth: 130,
							maxWidth: 130,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.latePaymentDays"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.latePaymentDays.tooltip"),
								showTooltipIcon: true,
							},
							headerClass: "align-right",
							field: "assessmentMeanLpd",
							cellClass: "align-right",
							valueFormatter: asDecimal({ showEmptyAsNotAvailable: true }),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentUnpaidCases",
							cellClass: "align-right",
							minWidth: 135,
							maxWidth: 135,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentUnpaidOverdueCases",
							cellClass: "align-right",
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentAverageDaysAfterDueDate",
							cellClass: "align-right",
							valueFormatter: asDecimal({ showEmptyAsNotAvailable: true }),
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentFullyPaidCases",
							cellClass: "align-right",
							minWidth: 140,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageLpdSettled"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageLpdSettled.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentAverageLpdSettled",
							cellClass: "align-right",
							valueFormatter: asDecimal({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentSoldAmount",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentSoldAmountUnpaid",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentSoldAmountUnpaidOverdue",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentSoldAmountFullyPaid",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay.tooltip"),
								showTooltipIcon: true
							},
							headerClass: "align-right",
							field: "assessmentSoldAmountFullyPaidInDelay",
							cellClass: "align-right",
							valueFormatter: asMoneyAmount({ showEmptyAsNotAvailable: true }),
							minWidth: 125,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.receivableDetails"),
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.docs"),
							headerClass: "no-padding align-center",
							cellClass: "no-padding align-center",
							cellRendererFramework: "GeneralActionIconButton",
							cellRendererParams: {
								iconStyleType: "material-icons",
								iconClass: "content_paste_search",
								action: (data) => this.showReceivableDetailsModal(data)
							},
							minWidth: 60,
						},
						{
							headerName: this.$t("investor.exchange.table.header.receivableAmount"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.receivableAmount.tooltip"),
								showTooltipIcon: true,
							},
							headerClass: "align-right",
							field: "receivableAmount",
							cellClass: "align-right",
							minWidth: 100,
							valueFormatter: asMoneyAmount(),
							sortable: true
						},
						{
							headerName: this.$t("common.vatRate"),
							headerClass: "align-right",
							field: "receivableVatRate",
							cellClass: "align-right",
							minWidth: 100,
							valueFormatter: asPercentage({ zeroDefault: "0%", showEmptyAsNotAvailable: true, decimals: 0 }),
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.receivableNetAmount"),
							headerClass: "align-right",
							field: "receivableNetAmount",
							cellClass: "align-right",
							minWidth: 125,
							valueFormatter: asMoneyAmount(),
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.dueDate"),
							field: "dueDate",
							minWidth: 150,
							valueFormatter: asLocalizedDate({ showDaysLeft: true }),
							sortable: true
						},
						{
							headerName: this.$t("common.applicableLaw.short"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.applicableLaw.tooltip"),
								showTooltipIcon: true,
							},
							field: "applicableLaw",
							minWidth: 110,
							maxWidth: 130,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.common.table.header.invoiceNumber"),
							field: "invoiceNumber",
							minWidth: 175,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							headerName: this.$t("common.contractNumber"),
							field: "installmentContractNumber",
							minWidth: 100,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							headerName: this.$t("investor.exchange.table.header.assignmentType"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.assignmentType.tooltip"),
								showTooltipIcon: true,
							},
							field: "postTrade",
							minWidth: 180,
							cellRendererFramework: "EnumAsText",
							enumDef: enumDef.postTradeType,
							columnGroupShow: "open"
						},
						{
							headerName: this.$t("investor.exchange.table.header.receivableType"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.receivableType.tooltip"), // TODO: Bundle - Get bundle params from config
							},
							field: "receivableType",
							minWidth: 75,
							maxWidth: 80,
							cellRendererFramework: "EnumAsIcon",
							enumDef: enumDef.receivableType,
							sortable: true,
							columnGroupShow: "open",
						},
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.salesOffer"),
					headerClass: "bg_special",
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.assignmentType.asIcon"),
							headerClass: "align-center",
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.assignmentType.asIcon.tooltip"),
								showTooltipIcon: true,
							},
							field: "postTrade",
							cellClass: "align-center",
							minWidth: 70,
							maxWidth: 70,
							cellRendererFramework: "EnumAsIcon",
							enumDef: enumDef.postTradeType,
							pinned: "right"
						},
						{
							headerName: this.$t("investor.exchange.table.header.retention"),
							headerClass: "align-center no-padding",
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.retention.tooltip"),
								showTooltipIcon: true,
							},
							field: "allowedRetentionRate",
							cellClass: "align-center no-padding",
							minWidth: 50,
							maxWidth: 50,
							valueFormatter: asPercentage({ showEmptyAsNotAvailable: true, decimals: 0 }),
							pinned: "right"
						},
						{
							headerName: this.$t("investor.exchange.table.header.currentRoi"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.currentRoi.tooltip"),
								showTooltipIcon: true,
							},
							headerClass: "align-right",
							field: "instantBuyRoi",
							cellClass: "align-right",
							minWidth: 70,
							maxWidth: 70,
							valueFormatter: asDecimal({ showPercentSign: true }),
							sortable: true,
							pinned: "right"
						},
						{
							hide: this.hideTrafficLight,
							headerName: this.$t("common.grade"),
							field: "trafficLightState",
							minWidth: 75,
							maxWidth: 75,
							cellRendererFramework: "ColorStatus",
							cellRendererParams: {
								color: (data) => enumDef.trafficLight.properties[data.trafficLightState]
							},
							sortable: true,
							pinned: "right"
						},
						{
							headerName: this.$t("investor.exchange.table.header.payoutAmount"),
							headerClass: "align-right no-padding",
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.payoutAmount.tooltip"),
								showTooltipIcon: true
							},
							field: "totalPrice",
							cellClass: "align-right no-padding",
							minWidth: 90,
							maxWidth: 90,
							valueFormatter: asMoneyAmount(),
							pinned: "right"
						},
						{
							headerName: this.$t("investor.exchange.table.header.purchaseFee"),
							headerClass: "align-right",
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.purchaseFee.tooltip"),
								showTooltipIcon: true
							},
							field: "walbingFeeAmount",
							cellClass: "align-right",
							minWidth: 90,
							maxWidth: 90,
							valueFormatter: asMoneyAmount(),
							pinned: "right"
						},
						{
							headerComponentFramework: "BiddingBuyButtonHeader",
							headerComponentParams: {
								bidOnAllSelected: this.placeAllSelectedBids,
							},
							minWidth: 90,
							maxWidth: 90,
							cellRendererFramework: "BiddingBuyButton",
							cellRendererParams: {
								bidOnSingle: this.placeBid,
							},
							pinned: "right"
						},
						{
							headerName: "<i class='fa fa-clock-o font_lg'></i>",
							headerClass: "align-center",
							field: "endTime",
							cellClass: "align-right",
							minWidth: 70,
							maxWidth: 70,
							cellRendererFramework: "TimeLeftCountdown",
							sortable: true,
							sort: "asc",
							pinned: "right"
						},
						{
							headerName: this.$t("investor.exchange.table.header.note"),
							field: "comment",
							minWidth: 70,
							maxWidth: 70,
							cellRendererFramework: "ReceivableInvestorNoteIcon",
							cellRendererParams: {
								showNoteModal: this.showExchangeReceivableNoteModel,
							},
							sortable: true,
							pinned: "right"
						},
						{
							minWidth: 45,
							maxWidth: 45,
							headerCheckboxSelection: true,
							checkboxSelection: true,
							headerCheckboxSelectionFilteredOnly: true,
							pinned: "right",
						}
					]
				}
			];
		},
		sortChanged(sortData) {
			this.sorting = sortData;
			this.getList();
		},
		getList() {
			let postData = {
				columns: [],
				filters: this.filters || [],
				maxResults: 25000,
				offset: 0,
				sorting: this.sorting
			};

			return new Promise((resolve, reject) => {
				exchangeReceivablesApi.inspection.listAuctions(this.activeAccountId, postData)
					.then((res) => {
						this.auctionList = res.data.map(item => {
							return {
								...item,
								// Make sure data for input field cell renderers and defaults are initialized before the table renders its data
								useInsurance: this.canBuyTci && !item.isInsuranceDisabled,
								comment: item.comment || null,
								totalPrice: null
							}
						}).map(item => {
							return {
								...item,
								// Set up the initial prices after we have all the defaults configured
								totalPrice: this.calculateTotalPrice(item),
							}
						});

						this.clearActionCheckedRowsInExchange();
						this.updateLastStatisticsUpdateTime();
						this.updateLastTCILimitsUpdateTime();
						resolve();
					})
					.catch((e) => {
						this.toast(e);
						reject();
					});
			});
		},
		showExchangeReceivableNoteModel(data) {
			this.$refs.exchangeReceivableNoteModal.showNoteModal(data);
		},
		showAlfaSettingsModal() {
			this.$refs.alfaSettingsModal.showForSettings(this.alfaStatus.id);
		},
		calculateTotalPrice(data) {
			return roundToScale(data.finalBuyPrice + (data.useInsurance ? data.insuranceGrossPremiumAmount : 0));
		},
		placeBid(data) {
			this.confirmPlaceBids([this.buildBid(data)]);
		},
		placeAllSelectedBids() {
			this.confirmPlaceBids(this.exchangeCheckedRows.map(this.buildBid), false);
		},
		buildBid(data) {
			return {
				data: data,
				amount: data.finalBuyPrice,
				feeAmount: data.walbingFeeAmount,
				type: enumDef.bidType.InstantBuy,
				retentionUsed: data.useRetention,
				withInsurance: data.useInsurance
			};
		},
		confirmPlaceBids(bids) {
			let companyCountry = this.currentUser.companyCountry;
			let bidInvolvingForeignCountries = bids.find(bid =>
				companyCountry !== bid.data.sellerCountry || companyCountry !== bid.data.applicableLaw
			);
			let bidsCount = bids.filter(bid => bid.type === enumDef.bidType.Bid).length;
			let instantBuysCount = bids.filter(bid => bid.type === enumDef.bidType.InstantBuy).length;
			let insuranceBuysCount = bids.filter(bid => bid.withInsurance).length;

			let msg = "";
			if (bidsCount > 0) {
				msg += this.$t("investor.exchange.table.operation.bid.confirm.msg.bids", { count: bidsCount });
			}
			if (bidsCount > 0 && instantBuysCount > 0) {
				msg += "<br/>";
			}
			if (instantBuysCount > 0) {
				msg += this.$t("investor.exchange.table.operation.bid.confirm.msg.instantBuys", { count: instantBuysCount });
			}
			if (bidInvolvingForeignCountries) {
				msg += "<br/><br/>"
					+ "<div class='light_bg_color'>"
					+ this.$t("investor.exchange.table.operation.bid.foreignCountry.confirm.msg")
					+ "</div>";
			}
			if (insuranceBuysCount > 0) {
				msg += this.$t("investor.exchange.table.operation.bid.confirm.msg.insuranceBuys", { count: insuranceBuysCount });
				msg += "<br/><br/>" +
					"<div class='text_left light_bg_color margin_top w_100'>" +
					this.$t("investor.exchange.table.note") +
					"</div>";
			}

			let option = {
				title: this.$t("investor.exchange.table.operation.bid.confirm.title"),
				msg: msg,
				confirmFun: this.sendBids,
				params: bids
			};
			this.confirmMeg(option, true);
		},
		sendBids(bids) {
			let bidsPostData = bids.map(bid => {
				return {
					auctionId: bid.data.auctionId,
					accountId: this.activeAccountId,
					amount: bid.amount,
					feeAmount: bid.feeAmount,
					type: bid.type,
					retentionUsed: bid.retentionUsed,
					withInsurance: bid.withInsurance
				};
			}, this);

			exchangeReceivablesApi.operation.placeBids(bidsPostData)
				.then((res) => {
					this.$refs.bidsResponseDialog.showWithResponse(res);
					this.getList();
				})
				.catch(this.toast);
		},
		onRowSelect(params) {
			this.setActionCheckedRowsInExchange(params.api.getSelectedRows());
		},
		applyFilter(filters) {
			this.filters = filters;
			this.getList();
		},
		updateLastStatisticsUpdateTime() {
			const STATISTICS_UPDATE_INTERVAL = 15;
			let lastIntervalMinutes = Math.floor(moment()
				.minutes() / STATISTICS_UPDATE_INTERVAL) * STATISTICS_UPDATE_INTERVAL;

			this.lastStatisticsUpdateTime = moment()
				.set("minute", lastIntervalMinutes)
				.format("HH:mm");
		},
		updateLastTCILimitsUpdateTime() {
			this.lastTCILimitsUpdateTime = moment()
				.format("HH:mm");
		},
		loadAlfaStatus() {
			if (!hasAccess(this.currentUser, enumDef.permissions.InvestorExchangeAlfa)) {
				return;
			}

			alfaApi.inspection.getCurrentStatus(this.activeAccountId)
				.then(res => {
					this.alfaStatus = res;
				})
				.catch(this.toast);
		},
		confirmStartAlfa() {
			let option = {
				title: this.$t("investor.exchange.alfa.start.confirm.title"),
				msg: this.buildHintBoxForConfirmation(this.$t("investor.exchange.alfa.start.confirm.msg", { stopButtonText: this.$t("investor.exchange.button.stopAlfa") }), false),
				confirmButtonText: this.$t("investor.exchange.button.startAlfa"),
				confirmButtonClass: "md-success",
				customClass: "msg_box_title_bold msg_box_width_l",
				confirmFun: this.startAlfa,
			};
			this.confirmMeg(option, true);
		},
		startAlfa() {
			alfaApi.operation.enable(this.alfaStatus.id)
				.then(() => this.alfaStatus.status = enumDef.alfaSettingsStatus.Active)
				.catch(this.toast);
		},
		stopAlfa() {
			alfaApi.operation.disable(this.alfaStatus.id)
				.then(() => this.alfaStatus.status = enumDef.alfaSettingsStatus.NotActive)
				.catch(this.toast);
		},
		checkMarketSoundingDecision(data) {
			let loadProspectiveReceivableSnapshotPromise = exchangeReceivablesApi.inspection.getMarketSoundingDecisionReceivableSnapshot(data.marketSoundingDecisionId);
			this.$refs.prospectiveReceivableSnapshotModal.showWithData(loadProspectiveReceivableSnapshotPromise);
		},
		showReceivableDetailsModal(data) {
			this.$store.dispatch("setActionNoticModal", {
				show: true,
				data: {
					title: this.$t("ReceDetail.Receivable.Details"),
					type: "ReceivableDetailsModal",
					width: 800,
					data: data
				}
			});
		}
	},
	watch: {
		showAllColumns() {
			this.$refs.exchangeReceivablesTable.showAllColumns(this.showAllColumns);
		},
		activeAccount() {
			this.setActionActiveAccount(this.activeAccount);
			this.loadAlfaStatus();
			this.getList();
		},
		ready() {
			if (this.ready) {
				this.setColumnDefs();
			}
		}
	},
};
</script>

<style lang="scss" scoped>
@import "src/components/templates/investor/exchange/exchange";
  // DEV-6132: Hide until we have TCI again
  /*.tcilogoandbutton{
    width: 700px;
    justify-content: inherit;
    align-items: center;
  }*/
</style>
